/**
 * Test weapons for the combat prototype (docs/COMBAT.md). Every attack has two phases:
 * hold the attack button to wind up (the longer, up to `chargeTime`, the stronger), then
 * release and the strike lands `strikeDelay` seconds later.
 *
 * Every weapon earns all three bonuses from how you move during the attack, scaled by its
 * affinity for each:
 * - finesse: changing the swing's direction mid-attack (a feint, a flick) instead of one
 *   smooth slash. It pierces armor, and it's how you get round a shield that reads your swing.
 * - rotation: turning your body as you swing. The most damage per second.
 * - movement: your speed toward the enemy during the attack (running in, coming down from a
 *   jump), and its speed toward you. Big single blows that stun and knock enemies flying.
 *
 * All numbers are first guesses, to be tuned by feel.
 */
export type WeaponKind = 'hammer' | 'sword' | 'spear';

export interface Affinity {
  finesse: number;
  rotation: number;
  movement: number;
  /** How hard a critical hits (its bonus × this): sharp points and edges more, blunt heads less. */
  crit: number;
}

export interface Weapon {
  id: string;
  name: string;
  /** Its shape and how it strikes: hammers and swords swing, spears thrust. */
  kind: WeaponKind;
  /** Damage before charge and the bonuses. */
  damage: number;
  /** Seconds of holding for a full charge. */
  chargeTime: number;
  /** Seconds from release to the hit. */
  strikeDelay: number;
  /** Seconds after the hit before the next windup. */
  recovery: number;
  /**
   * Reach from the eye (m), and how thick the weapon hits (m either side of its line): a
   * hammer's head, a blade's width, a spear's point. A little further off grazes (GLANCE).
   */
  reach: number;
  blade: number;
  /** How much each bonus counts with this weapon. */
  affinity: Affinity;
  /** How hard it hits things aside: scales knockback and stun. */
  weight: number;
  /** Knockback speed for 80 kg at impact 1 (m/s). */
  knockback: number;
}

/**
 * While attacking (the windup, and from the release to the hit), the view (and so the swing)
 * turns at most this fast (rad/s): LOOK_RATE / √strikeDelay. Slower weapons swing slower,
 * but over their longer strike they can still turn further in all: a sword 3.3 rad/s for
 * 0.14 s (27°), a maul 1.8 rad/s for 0.5 s (51°).
 */
const LOOK_RATE = 1.25;

export function swingLookRate(w: Weapon): number {
  return LOOK_RATE / Math.sqrt(w.strikeDelay);
}

/** The most the view can turn during the weapon's strike (radians): its swing budget. */
export function strikeSwing(w: Weapon): number {
  return swingLookRate(w) * w.strikeDelay;
}

export const WEAPONS: Weapon[] = [
  {
    id: 'hammer',
    name: 'War hammer',
    kind: 'hammer',
    damage: 16,
    chargeTime: 0.9,
    strikeDelay: 0.28,
    recovery: 0.4,
    reach: 2.5,
    blade: 0.3,
    affinity: { finesse: 0.55, rotation: 0.65, movement: 1.1, crit: 0.6 },
    weight: 1.2,
    knockback: 2.4,
  },
  {
    id: 'maul',
    name: 'Maul (slow)',
    kind: 'hammer',
    damage: 22,
    chargeTime: 1.1,
    strikeDelay: 0.5,
    recovery: 0.6,
    reach: 2.8,
    blade: 0.35,
    affinity: { finesse: 0.45, rotation: 0.5, movement: 1.3, crit: 0.5 },
    weight: 1.7,
    knockback: 2.6,
  },
  {
    id: 'sword',
    name: 'Sword',
    kind: 'sword',
    damage: 12,
    chargeTime: 0.6,
    strikeDelay: 0.14,
    recovery: 0.25,
    reach: 2.4,
    blade: 0.22,
    affinity: { finesse: 0.85, rotation: 1.1, movement: 0.6, crit: 1.2 },
    weight: 0.7,
    knockback: 2,
  },
  {
    id: 'greatsword',
    name: 'Greatsword',
    kind: 'sword',
    damage: 16,
    chargeTime: 0.8,
    strikeDelay: 0.22,
    recovery: 0.4,
    reach: 2.9,
    blade: 0.28,
    affinity: { finesse: 0.65, rotation: 1.2, movement: 0.85, crit: 1.0 },
    weight: 1.1,
    knockback: 2.3,
  },
  {
    id: 'spear',
    name: 'Spear',
    kind: 'spear',
    damage: 13,
    chargeTime: 0.7,
    strikeDelay: 0.19,
    recovery: 0.3,
    reach: 3.5,
    blade: 0.12,
    affinity: { finesse: 1.15, rotation: 0.45, movement: 1.25, crit: 1.3 },
    weight: 0.9,
    knockback: 2,
  },
];
